import { eq } from 'drizzle-orm'
import { useState } from 'react'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	data,
	redirect,
	useActionData,
	useFetcher,
	useLoaderData,
} from 'react-router'
import { FloatingLabelField, FloatingSelectField } from '#app/components/forms'
import { ScreeningConsentModal } from '#app/components/screening/ScreeningConsentModal'
import { Button } from '#app/components/ui/button'
import { Label } from '#app/components/ui/label'
import { RadioGroup, RadioGroupItem } from '#app/components/ui/radio-group'
import { generateAuthorizeToken } from '#app/services/flinks.server'
import { db } from '#app/utils/db.server'
import { getDomainUrl } from '#app/utils/misc'
import { getSession } from '#app/utils/session.server'
import { flinksTempApplications } from '#db/schema/flinksTempApplication'
import { properties } from '#db/schema/properties'

type ActionData =
	| {
			success: true
			applicationId: string
			flinksConnectUrl: string
			token: string
			redirectUrl: string
	  }
	| { error: string }
	| null

// Helper function to get tenantId from hostname
async function getTenantIdFromHostname(hostname: string): Promise<string> {
	const existingTenant = await db.query.tenant.findFirst({
		where: (tenant, { eq }) => eq(tenant.hostname, hostname),
	})

	if (existingTenant) {
		return existingTenant.id
	}

	// Fallback to TrueTenant or default tenant
	const trueTenant = await db.query.tenant.findFirst({
		where: (tenant, { eq, or: orFn }) =>
			orFn(
				eq(tenant.name, 'TrueTenant'),
				eq(tenant.hostname, 'localhost'),
				eq(tenant.hostname, 'truetenant.ai'),
			),
	})

	if (!trueTenant) {
		throw new Error(
			'No tenant found for this hostname. Please ensure tenant is configured.',
		)
	}

	return trueTenant.id
}

export async function loader({ request }: LoaderFunctionArgs) {
	// Check if in production and if user has flinks access
	const isProd = process.env.NODE_ENV === 'production'
	if (isProd) {
		const session = await getSession(request.headers.get('Cookie'))
		const flinksUnlocked = session.get('flinksUnlocked') === true
		const isUnlocked = session.get('unlocked') === true

		// If not fully unlocked and doesn't have flinks access, redirect to home
		// (root.tsx will show password form)
		if (!isUnlocked && !flinksUnlocked) {
			throw redirect('/')
		}
	}

	try {
		const hostname = new URL(request.url).hostname
		const tenantId = await getTenantIdFromHostname(hostname)

		// Fetch active properties/buildings for dropdown
		const dbProperties = await db.query.properties.findMany({
			where: eq(properties.tenantId, tenantId),
			orderBy: (properties, { desc }) => [desc(properties.createdAt)],
		})

		// Transform to dropdown options
		const propertyOptions = dbProperties
			.filter((p) => p.status === 'active')
			.map((p) => ({
				label: p.propertyName || p.address || 'Unknown Property',
				value: p.id,
			}))

		return {
			properties: propertyOptions,
		}
	} catch (error) {
		console.error('[Flinks Connect] Error in loader:', error)
		return {
			properties: [],
			error:
				error instanceof Error ? error.message : 'Failed to load properties',
		}
	}
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	const fullName = formData.get('fullName')?.toString()
	const buildingId = formData.get('buildingId')?.toString()
	const unitNumber = formData.get('unitNumber')?.toString()
	const currentRent = formData.get('currentRent')?.toString()
	const payByElectronicFundTransfer = formData
		.get('payByElectronicFundTransfer')
		?.toString()
	const consentAccepted = formData.get('consentAccepted')?.toString() === 'true'

	// Validation
	if (!fullName || !buildingId || !unitNumber || !currentRent) {
		return data({ error: 'All fields are required' }, { status: 400 })
	}

	if (!consentAccepted) {
		return data({ error: 'Consent must be accepted' }, { status: 400 })
	}

	const rentAmount = Number.parseFloat(currentRent)
	if (Number.isNaN(rentAmount) || rentAmount <= 0) {
		return data(
			{ error: 'Current rent must be a valid positive number' },
			{ status: 400 },
		)
	}

	try {
		const hostname = new URL(request.url).hostname
		const tenantId = await getTenantIdFromHostname(hostname)

		// Generate Flinks authorize token
		const flinksToken = await generateAuthorizeToken()

		// Save application to database
		const [application] = await db
			.insert(flinksTempApplications)
			.values({
				userId: null, // Unauthenticated user
				tenantId,
				fullName,
				buildingId,
				unitNumber,
				currentRent: rentAmount,
				payByElectronicFundTransfer: payByElectronicFundTransfer || null,
				consentAccepted: true,
				consentAcceptedAt: new Date(),
				flinksToken,
				status: 'pending',
			})
			.returning({ id: flinksTempApplications.id })

		if (!application) {
			return data({ error: 'Failed to create application' }, { status: 500 })
		}

		// Build redirect URL with applicationId
		const baseUrl = getDomainUrl(request)
		const redirectUrl = `${baseUrl}/flinks-success?applicationId=${application.id}`

		const flinksConnectUrl = process.env.FLINKS_CONNECT_URL

		if (!flinksConnectUrl) {
			return data(
				{ error: 'Flinks Connect URL not configured' },
				{ status: 500 },
			)
		}
		console.log('flinksConnectUrl', flinksConnectUrl)
		// Return data instead of redirect so we can show the iframe
		return data({
			success: true,
			applicationId: application.id,
			flinksConnectUrl,
			token: flinksToken,
			redirectUrl,
		})
	} catch (error) {
		console.error('[Flinks Connect] Error:', error)
		return data(
			{
				error:
					error instanceof Error
						? error.message
						: 'Failed to process application. Please try again.',
			},
			{ status: 500 },
		)
	}
}

export default function FlinksConnectRoute() {
	const { properties } = useLoaderData<typeof loader>()
	const actionData = useActionData<ActionData>()
	const fetcher = useFetcher<ActionData>()

	const [showConsentModal, setShowConsentModal] = useState(false)
	const [formData, setFormData] = useState({
		fullName: '',
		buildingId: '',
		unitNumber: '',
		currentRent: '',
		payByElectronicFundTransfer: '',
	})
	const [errors, setErrors] = useState<Record<string, string>>({})

	// Check both actionData (direct form submission) and fetcher.data (fetcher submission)
	const successData =
		fetcher.data && 'success' in fetcher.data && fetcher.data.success
			? fetcher.data
			: actionData && 'success' in actionData && actionData.success
				? actionData
				: null

	// If we have success data from action, show Flinks iframe
	if (successData) {
		const isStaging =
			(typeof globalThis.window === 'undefined'
				? process.env.NODE_ENV
				: globalThis.ENV?.MODE) !== 'production'
		let iframeSrc = ''
		if (isStaging) {
			iframeSrc = `${successData.flinksConnectUrl}&redirectUrl=${encodeURIComponent(successData.redirectUrl)}&authorizeToken=${successData.token}`
		} else {
			iframeSrc = `${successData.flinksConnectUrl}/?redirectUrl=${encodeURIComponent(successData.redirectUrl)}&authorizeToken=${successData.token}&daysOfTransactions=Days365`
		}

		return (
			<div style={styles.wrapper}>
				<iframe
					title="Flinks Connect Window"
					src={iframeSrc}
					style={styles.iframe}
					allow="fullscreen"
				/>
			</div>
		)
	}

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }))
		// Clear error for this field when user starts typing
		if (errors[field]) {
			setErrors((prev) => {
				const newErrors = { ...prev }
				delete newErrors[field]
				return newErrors
			})
		}
	}

	const validateForm = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.fullName.trim()) {
			newErrors.fullName = 'Full name is required'
		}
		if (!formData.buildingId) {
			newErrors.buildingId = 'Building selection is required'
		}
		if (!formData.unitNumber.trim()) {
			newErrors.unitNumber = 'Unit number is required'
		}
		if (!formData.currentRent.trim()) {
			newErrors.currentRent = 'Current rent is required'
		} else {
			const rentAmount = Number.parseFloat(formData.currentRent)
			if (Number.isNaN(rentAmount) || rentAmount <= 0) {
				newErrors.currentRent = 'Current rent must be a valid positive number'
			}
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		if (!validateForm()) {
			return
		}

		// Show consent modal instead of submitting directly
		setShowConsentModal(true)
	}

	const handleConsent = () => {
		// Close modal first
		setShowConsentModal(false)

		// Prepare form data for submission
		const submitFormData = new FormData()
		submitFormData.append('fullName', formData.fullName)
		submitFormData.append('buildingId', formData.buildingId)
		submitFormData.append('unitNumber', formData.unitNumber)
		submitFormData.append('currentRent', formData.currentRent)
		submitFormData.append(
			'payByElectronicFundTransfer',
			formData.payByElectronicFundTransfer,
		)
		submitFormData.append('consentAccepted', 'true')

		// Submit using fetcher
		void fetcher.submit(submitFormData, {
			method: 'POST',
		})
	}

	// Show error if there is one (check fetcher.data first since we're using fetcher)
	const displayError =
		fetcher.data && 'error' in fetcher.data
			? fetcher.data.error
			: actionData && 'error' in actionData
				? actionData.error
				: null

	return (
		<div className="min-h-screen bg-gray-50 py-8 px-4">
			<div className="max-w-2xl mx-auto">
				<div className="bg-white rounded-lg shadow-md p-8">
					<h1 className="text-2xl font-semibold text-gray-900 mb-6">
						Application Information
					</h1>

					{displayError && (
						<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
							<p className="text-red-600">{displayError}</p>
						</div>
					)}

					<fetcher.Form method="POST" onSubmit={handleFormSubmit}>
						<div className="space-y-6">
							<FloatingLabelField
								labelProps={{ children: 'Full Name' }}
								inputProps={{
									name: 'fullName',
									type: 'text',
									value: formData.fullName,
									onChange: (e) =>
										handleInputChange('fullName', e.target.value),
									placeholder: 'Enter your full name',
									required: true,
								}}
								errors={errors.fullName ? [errors.fullName] : undefined}
							/>

							<FloatingSelectField
								labelProps={{ children: 'Building' }}
								value={formData.buildingId}
								onChange={(value) => handleInputChange('buildingId', value)}
								options={properties}
								errors={errors.buildingId ? [errors.buildingId] : undefined}
							/>

							<FloatingLabelField
								labelProps={{ children: 'Unit Number' }}
								inputProps={{
									name: 'unitNumber',
									type: 'text',
									value: formData.unitNumber,
									onChange: (e) =>
										handleInputChange('unitNumber', e.target.value),
									placeholder: 'Enter unit number',
									required: true,
								}}
								errors={errors.unitNumber ? [errors.unitNumber] : undefined}
							/>

							<FloatingLabelField
								labelProps={{ children: 'Current Rent Amount' }}
								inputProps={{
									name: 'currentRent',
									type: 'number',
									step: '0.01',
									min: '0',
									value: formData.currentRent,
									onChange: (e) =>
										handleInputChange('currentRent', e.target.value),
									placeholder: 'Enter current rent amount',
									required: true,
								}}
								errors={errors.currentRent ? [errors.currentRent] : undefined}
							/>

							<div className="space-y-2">
								<Label className="text-[16px] text-[#32302F]">
									Pay by Electronic Fund Transfer
								</Label>
								<RadioGroup
									value={formData.payByElectronicFundTransfer}
									onValueChange={(value) =>
										handleInputChange('payByElectronicFundTransfer', value)
									}
									className="flex gap-6"
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="yes" id="eft-yes" />
										<Label
											htmlFor="eft-yes"
											className="text-[16px] text-[#484848] cursor-pointer"
										>
											Yes
										</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="no" id="eft-no" />
										<Label
											htmlFor="eft-no"
											className="text-[16px] text-[#484848] cursor-pointer"
										>
											No
										</Label>
									</div>
								</RadioGroup>
								{errors.payByElectronicFundTransfer && (
									<p className="text-red-500 text-sm mt-1">
										{errors.payByElectronicFundTransfer}
									</p>
								)}
							</div>

							<div className="flex justify-end pt-4">
								<Button
									type="submit"
									disabled={fetcher.state === 'submitting'}
									className="bg-[#32302F] hover:bg-[#1a1918] text-white px-8 py-3 rounded-[30px] font-medium text-[16px]"
								>
									{fetcher.state === 'submitting'
										? 'Processing...'
										: 'Continue to Bank Verification'}
								</Button>
							</div>
						</div>
					</fetcher.Form>
				</div>
			</div>

			{showConsentModal && (
				<ScreeningConsentModal
					onConsent={handleConsent}
					onClose={() => setShowConsentModal(false)}
				/>
			)}
		</div>
	)
}

const styles = {
	wrapper: {
		width: '100vw',
		height: '100vh',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		overflow: 'hidden',
		margin: 0,
		padding: 0,
	},
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
}
